import { Link } from "react-router-dom";
import {
  Sparkles,
  HeartHandshake,
  Hammer,
  Bug,
  Wind,
  Calendar,
} from "lucide-react";
import { Seo, breadcrumbSchema, faqSchema, serviceSchema } from "../components/Seo";
import { PageHero } from "../components/PageHero";
import { useSiteContent } from "../lib/useSiteContent";
import { ChecklistSection } from "../components/ChecklistSection";
import { WhyChooseUs } from "../components/WhyChooseUs";
import { ProcessSteps } from "../components/ProcessSteps";
import { FaqAccordion } from "../components/FaqAccordion";
import { RelatedLinks } from "../components/RelatedLinks";
import { ServiceCTA } from "../components/ServiceCTA";

const PATH = "/deep-cleaning-london";

const DEFAULT_FAQS = [
  {
    question:
      "What is the difference between deep cleaning and regular cleaning in London?",
    answer:
      "A regular clean handles visible surfaces — counters, floors, bathrooms — on a recurring weekly or fortnightly basis. A deep clean in London goes after the build-up regular cleans leave behind: scale around taps and tile grout, grease behind the cooker, dust on top of cabinets, marks on skirting boards, soap residue in shower screens and inside-cupboard wipe-downs. It is a longer, slower, more detailed job that resets the entire property.",
  },
  {
    question: "How often should I book a one-off deep clean in London?",
    answer:
      "Most London residents book a one-off deep clean every six to twelve months, plus an extra deep clean after building works, after a long winter, before hosting guests for the holidays, or before a property valuation. Households with pets, allergies or asthma usually opt for a quarterly deep clean to stay on top of dust and dander.",
  },
  {
    question: "How long does professional deep cleaning take in London?",
    answer:
      "A studio takes 4 to 5 hours, a one-bedroom flat 5 to 7 hours, and larger family homes can be a full day with a team of three cleaners. Because every surface — including inside cupboards, behind appliances and around skirting — is touched by hand, we never sacrifice time to hit a number.",
  },
  {
    question: "Do professional deep cleaners bring their own equipment?",
    answer:
      "Yes. Our deep cleaning teams arrive with hospital-grade degreasers, descalers, microfibre cloth packs, HEPA vacuums, scale-removing scrub pads, telescopic dusters for ceilings and cornices, and steam machines for grout. You do not need to provide anything beyond water and electricity.",
  },
  {
    question:
      "Can deep cleaning help if I have asthma or allergies in London?",
    answer:
      "Yes. A proper deep clean removes the dust mites, pet dander, pollen residue and mould spores that build up in soft furnishings, skirting and behind furniture. We use fragrance-free, asthma-friendly products on request and HEPA-filter vacuums so we capture allergens rather than redistribute them.",
  },
  {
    question:
      "Is a deep clean enough for end of tenancy or do I need a separate service?",
    answer:
      "A deep clean covers most of what an inventory check looks for, but a checkout report often includes specifics — like the inside of every cupboard, the freezer compartment defrosted, and the oven dip-tanked — that we separate into a dedicated end of tenancy clean. If your move-out is approaching, our specialised end of tenancy cleaning in London is the safer booking.",
  },
];

const CHECKLIST_GROUPS = [
  {
    title: "Kitchen Reset",
    items: [
      "Hand-degrease cabinet doors, handles and side panels",
      "Wipe inside every cupboard and drawer",
      "Clean behind and underneath kitchen appliances where movable",
      "Descale taps, sink, splashback and tile grout",
      "Polish hob, extractor grilles and stainless steel surfaces",
      "Dust tops of cabinets, cornicing and pendant lights",
    ],
  },
  {
    title: "Bathroom Restoration",
    items: [
      "Strip limescale from shower heads, screens and tile joints",
      "Restore grout colour with steam and detail brushes",
      "Refresh silicone seals around bath, sink and basin",
      "Hand-clean towel rails, radiators and extractor fans",
      "Sanitise toilet seat, hinges, base and behind the pan",
      "Polish mirrors, glass shelving and chrome fittings",
    ],
  },
  {
    title: "Living Spaces & Bedrooms",
    items: [
      "Dust tops of doors, picture rails, cornices and curtain poles",
      "Hand-wipe skirting boards, dado rails and door frames",
      "Vacuum and detail underneath beds and large furniture",
      "Spot-clean fabric sofas and dust hard furniture",
      "Polish wood floors and detail edges along skirting",
      "Wipe radiators front, top and inside vents",
    ],
  },
  {
    title: "Whole-Home Detailing",
    items: [
      "HEPA vacuum carpets, rugs and runners thoroughly",
      "Clean interior windows, frames, sills and tracks",
      "Wipe every light switch, socket and door handle",
      "Dust ceiling vents, smoke alarms and lampshades",
      "Tidy and clean inside-front of storage cupboards",
      "Final scented-but-low-allergen finish across the home",
    ],
  },
];

const getWhyChoose = (sc) => [
  { icon: Sparkles,     title: sc.why1_title || "Top-to-bottom specification", description: sc.why1_desc || "We work ceiling-down and outside-in so dust falls onto surfaces we have not yet touched — a level of method most one-off cleaners skip." },
  { icon: HeartHandshake,title: sc.why2_title || "Honest, fixed pricing",     description: sc.why2_desc || "A flat all-in quote based on bedrooms and condition. No surprise add-ons for limescale, grease or pet hair on the day." },
  { icon: Hammer,       title: sc.why3_title || "Post-renovation ready",      description: sc.why3_desc || "After-builders dust, plaster residue and paint flecks need specialist removal — our deep clean teams are trained on post-construction work." },
  { icon: Bug,          title: sc.why4_title || "Allergy-conscious products", description: sc.why4_desc || "HEPA vacuums, fragrance-free degreasers and microfibre-only cloths reduce dust mite, pet dander and pollen load for sensitive households." },
  { icon: Wind,         title: sc.why5_title || "Air-quality focus",          description: sc.why5_desc || "We dust ceiling vents, extractor grilles and radiator backs — the surfaces that recycle dust back into the air every time the heating kicks on." },
  { icon: Calendar,     title: sc.why6_title || "Flexible scheduling",        description: sc.why6_desc || "Evening, weekend and split-session slots so a full deep clean does not require you to take a day off work." },
];

const getProcess = (sc) => [
  { title: sc.step1_title || "Walkthrough quote", description: sc.step1_desc || "Share the property size, the rooms that need the most attention and any condition notes. We send a fixed quote within hours." },
  { title: sc.step2_title || "Pre-clean prep",    description: sc.step2_desc || "On the day, we move light furniture aside, set up HEPA vacuums and prepare each room with the correct chemicals for its surfaces." },
  { title: sc.step3_title || "Detail clean",      description: sc.step3_desc || "Top-down, outside-in. We work room by room with two to three cleaners so every surface is touched without rushing the detail work." },
  { title: sc.step4_title || "Walk and review",   description: sc.step4_desc || "We walk the property with you and refresh anything that does not meet the standard before we leave. No bills until you are happy." },
];

const DeepCleaningLondon = () => {
  const sc = useSiteContent("deep", {
    eyebrow: "Deep Cleaning London", h1: "One-Off Deep Cleaning", highlight: "for London Homes",
    subheading: "A top-to-bottom reset for the surfaces a weekly clean never touches — inside cupboards, behind appliances, tile grout, tops of doors and ceiling vents.",
    overview_eyebrow: "Service Overview", overview_heading: "The professional deep clean Londoners book when \"tidy\" isn't enough",
    overview_p1: "London homes accumulate grime faster than most British cities. The Underground throws fine soot into the air, central heating pulls dust through every radiator vent, and hard London water leaves a chalky limescale layer over taps, kettles and shower screens within weeks of a regular clean. Our one off deep cleaning in London is the periodic reset that pulls all of that buildup out of the property in a single visit.",
    overview_p2: "We are not a recurring weekly cleaner — we are a specialist deep cleaning company. Every visit is staffed by a small team so a typical home is finished in one day rather than spread over multiple light visits. Tenants book us for spring cleans and pre-Christmas resets; landlords use us mid-tenancy and during gaps between long-term lets; and homeowners book us after building works, before listing the property for sale, or after a particularly dusty winter.",
    overview_p3: "If your booking is tied to a move-out date, our specialist end of tenancy cleaning in London is a better fit — it adds the inventory-specific items a checkout report demands. For everything else, a one-off deep clean is the cleanest, most cost-effective way to reset a London home.",
    checklist_eyebrow: "What's Included", checklist_heading: "A 4-zone professional deep clean specification", checklist_sub: "Each zone is treated with a different method and chemistry, because greasy kitchens, mineral-heavy bathrooms, fabric-rich bedrooms and high-touch hallways all wear differently in a London home.",
    why_eyebrow: "Why Book Us", why_heading: "London's most thorough one off deep cleaners", why_sub: "A deep clean is only worth booking if it actually lasts. The way we work — top-down, outside-in, surface-specific chemistry — is why our clients re-book us six months later rather than monthly.",
    process_eyebrow: "The Process", process_heading: "How a Prime Tenancy deep clean runs", process_sub: "From the moment you message us to the final walk-through, the entire booking takes about a week — and the clean itself is done in a single day.",
    spec_eyebrow: "When to book", spec_heading: "The right moment for a London deep clean",
    spec_p1: "A deep clean is a periodic investment, not a recurring service. Most Londoners get the most value by booking around a specific trigger: a season change, a renovation, a house-move, or simply a year of regular cleans piling up under the radar. We adjust the chemistry and the team size based on the trigger so the booking is right-sized.",
    spec_p2: "If you live with pets or allergies in a flat with carpets, pair the deep clean with our carpet cleaning service in London for an even bigger reset.",
    spec_sidebar: "Popular booking triggers",
    faq_heading: "Deep cleaning London FAQs", faq_sub: "What clients ask before booking a one-off professional deep clean.",
    faq1_q: DEFAULT_FAQS[0].question, faq1_a: DEFAULT_FAQS[0].answer,
    faq2_q: DEFAULT_FAQS[1].question, faq2_a: DEFAULT_FAQS[1].answer,
    faq3_q: DEFAULT_FAQS[2].question, faq3_a: DEFAULT_FAQS[2].answer,
    faq4_q: DEFAULT_FAQS[3].question, faq4_a: DEFAULT_FAQS[3].answer,
    faq5_q: DEFAULT_FAQS[4].question, faq5_a: DEFAULT_FAQS[4].answer,
    faq6_q: DEFAULT_FAQS[5].question, faq6_a: DEFAULT_FAQS[5].answer,
    cta_title: "Book your London deep clean", cta_desc: "Fixed pricing, allergy-friendly products and a one-day finish for most flats. Get your quote now.",
  });
  const WHY_CHOOSE = getWhyChoose(sc);
  const PROCESS    = getProcess(sc);
  const FAQS = [
    { question: sc.faq1_q, answer: sc.faq1_a }, { question: sc.faq2_q, answer: sc.faq2_a },
    { question: sc.faq3_q, answer: sc.faq3_a }, { question: sc.faq4_q, answer: sc.faq4_a },
    { question: sc.faq5_q, answer: sc.faq5_a }, { question: sc.faq6_q, answer: sc.faq6_a },
  ];
  return (
    <>
      <Seo
        title="Deep Cleaning London — One Off Deep Cleaners"
        description="Professional deep cleaning London. One-off deep cleans for homes, post-renovation and seasonal resets. Fully insured, allergy-friendly products, weekend slots."
        ogTitle="Deep Cleaning London | Professional Deep Cleaners"
        ogDescription="Top-to-bottom one off deep cleaning by an insured London team. Allergy-conscious products, fixed pricing."
        path={PATH}
        schema={[
          breadcrumbSchema([
            { name: "Services", path: PATH },
            { name: "Deep Cleaning London", path: PATH },
          ]),
          faqSchema(FAQS),
          serviceSchema({
            name: "Deep Cleaning London",
            serviceType: "Deep Cleaning",
            description:
              "Professional one-off deep cleaning across all London boroughs — top-to-bottom detailing for homes, post-renovation properties and seasonal resets.",
          }),
        ]}
      />

      <PageHero
        eyebrow={sc.eyebrow}
        h1={sc.h1}
        highlight={sc.highlight}
        subheading={sc.subheading}
        breadcrumbs={[
          { name: "Services", path: PATH },
          { name: "Deep Cleaning London", path: PATH },
        ]}
        image="https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=1920&q=80"
      />

      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto px-6 md:px-12 lg:px-20">
          <div className="max-w-3xl">
            <span className="text-teal font-semibold text-sm uppercase tracking-wider">{sc.overview_eyebrow}</span>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-navy mt-3 mb-6 leading-tight">{sc.overview_heading}</h2>
            <div className="text-slate-700 text-base md:text-lg leading-relaxed space-y-5">
              <p>{sc.overview_p1}</p>
              <p>{sc.overview_p2}</p>
              <p>
                {sc.overview_p3}{" "}<Link to="/end-of-tenancy-cleaning-london" className="text-teal font-medium hover:text-navy">end of tenancy cleaning in London</Link>.
              </p>
            </div>
          </div>
        </div>
      </section>

      <ChecklistSection eyebrow={sc.checklist_eyebrow} heading={sc.checklist_heading} subheading={sc.checklist_sub} groups={CHECKLIST_GROUPS} background="slate" />
      <WhyChooseUs eyebrow={sc.why_eyebrow} heading={sc.why_heading} subheading={sc.why_sub} items={WHY_CHOOSE} />
      <ProcessSteps eyebrow={sc.process_eyebrow} heading={sc.process_heading} subheading={sc.process_sub} steps={PROCESS} background="slate" />

      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 max-w-6xl mx-auto items-start">
            <div>
              <span className="text-teal font-semibold text-sm uppercase tracking-wider">{sc.spec_eyebrow}</span>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-navy mt-3 mb-5 leading-tight">{sc.spec_heading}</h2>
              <p className="text-slate-700 leading-relaxed mb-4">{sc.spec_p1}</p>
              <p className="text-slate-700 leading-relaxed">
                {sc.spec_p2}{" "}<Link to="/carpet-cleaning-london" className="text-teal font-medium hover:text-navy">carpet cleaning service in London</Link>{" "}for an even bigger reset.
              </p>
            </div>

            <div className="bg-slate-50 rounded-2xl p-8 md:p-10 border border-slate-100">
              <h3 className="font-heading text-xl font-semibold text-navy mb-5">{sc.spec_sidebar}</h3>
              <ul className="space-y-3">
                {[
                  "Pre-Christmas reset before hosting family",
                  "Spring clean after a long winter of heating",
                  "Post-renovation dust removal",
                  "Pre-listing clean before selling or letting",
                  "Mid-tenancy maintenance for landlords",
                  "After welcoming a new baby home",
                  "After a pet has shed heavily through the year",
                  "Before or after long-term Airbnb hosting",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-gold mt-2 flex-shrink-0" />
                    <span className="text-slate-700 text-sm leading-relaxed">
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <FaqAccordion heading={sc.faq_heading} subheading={sc.faq_sub} faqs={FAQS} background="slate" />
      <RelatedLinks currentPath={PATH} />
      <ServiceCTA title={sc.cta_title} description={sc.cta_desc} variant="navy" />
    </>
  );
};

export default DeepCleaningLondon;
